import React from "react";
import {
  Col,
  Row,
  Input,
  Label,
  FormFeedback,
} from "reactstrap";
import Select from "react-select";

interface MenuPermissionFormProps {
  validation: any;
  menus?: any[];
  permissions?: any[];
  menuPermissions?: any[];
  selectedMenuId?: number | null;
  selectedPermissionId?: number | null;
}

const MenuPermissionForm: React.FC<MenuPermissionFormProps> = ({
  validation,
  menus = [],
  permissions = [],
  menuPermissions = [],
  selectedMenuId = null,
  selectedPermissionId = null,
}) => {
  // Filter to show only active menus
  const activeMenus = React.useMemo(() => {
    if (!Array.isArray(menus)) return [];
    return menus.filter((menu: any) => 
      menu.status?.toLowerCase() === "active"
    );
  }, [menus]);

  const menuOptions = activeMenus.map((menu) => ({
    label: menu.title || menu.menuname,
    value: menu.menuid,
  }));

  // Filter to show only active permissions
  const activePermissions = React.useMemo(() => {
    if (!Array.isArray(permissions)) return [];
    return permissions.filter((permission: any) => 
      permission.status?.toLowerCase() === "active"
    );
  }, [permissions]);

  // Get the selected menu ID from form or prop
  const currentMenuId = React.useMemo(() => {
    return Number(validation.values.menuid || selectedMenuId) || null;
  }, [validation.values.menuid, selectedMenuId]);

  // Filter out permissions that already have a combination with the selected menu
  const availablePermissions = React.useMemo(() => {
    if (!currentMenuId) return activePermissions;
    
    // Get permission IDs that already have a combination with the selected menu
    const existingPermissionIds = menuPermissions
      .filter((menuPermission: any) => Number(menuPermission.menuid) === Number(currentMenuId))
      .map((menuPermission: any) => Number(menuPermission.permissionid));
    
    // Filter out permissions that already exist for this menu
    // But keep the currently selected permission (if editing)
    return activePermissions.filter((permission: any) => {
      const permissionId = Number(permission.permissionid);
      const isCurrentlySelected = permissionId === Number(validation.values.permissionid || selectedPermissionId);
      return !existingPermissionIds.includes(permissionId) || isCurrentlySelected;
    });
  }, [activePermissions, currentMenuId, menuPermissions, validation.values.permissionid, selectedPermissionId]);

  const permissionOptions = availablePermissions.map((permission) => ({
    label: permission.permissionname,
    value: permission.permissionid,
  }));

  return (
    <Row className="g-3">
      <Col md={12}>
        <Label className="fw-bold mb-1">
          Menu <span className="text-danger">*</span>
        </Label>
        <div style={{ width: "100%" }}>
          <Select
            options={menuOptions}
            name="menuid"
            placeholder="Select Menu"
            value={menuOptions.find((opt) => {
              const formValue = validation.values.menuid;
              const selectedValue = selectedMenuId;
              return opt.value === Number(formValue || selectedValue);
            }) || null}
            onChange={(e: { value: any }) => {
              validation.setFieldValue("menuid", e?.value);
              // Clear permissionid when menu changes to prevent invalid combinations
              validation.setFieldValue("permissionid", "");
              validation.setFieldTouched("permissionid", false);
            }}
            onBlur={() => validation.setFieldTouched("menuid", true)}
            classNamePrefix="react-select"
            className={validation.touched.menuid && validation.errors.menuid ? "react-select-error" : ""}
            isClearable
          />
        </div>
        {validation.touched.menuid && validation.errors.menuid && (
          <div className="invalid-feedback d-block">{validation.errors.menuid}</div>
        )}
      </Col>

      <Col md={12}>
        <Label className="fw-bold mb-1">
          Permission <span className="text-danger">*</span>
        </Label>
        <div style={{ width: "100%" }}>
          <Select
            options={permissionOptions}
            name="permissionid"
            placeholder="Select Permission"
            value={permissionOptions.find((opt) => {
              const formValue = validation.values.permissionid;
              const selectedValue = selectedPermissionId;
              return opt.value === Number(formValue || selectedValue);
            }) || null}
            onChange={(e: { value: any }) => {
              validation.setFieldValue("permissionid", e?.value);
              // Clear menuid error when permission changes to allow re-validation
              if (validation.errors.menuid && validation.touched.menuid) {
                validation.setFieldTouched("menuid", false);
              }
            }}
            onBlur={() => validation.setFieldTouched("permissionid", true)}
            classNamePrefix="react-select"
            className={validation.touched.permissionid && validation.errors.permissionid ? "react-select-error" : ""}
            isClearable
          />
        </div>
        {validation.touched.permissionid && validation.errors.permissionid && (
          <div className="invalid-feedback d-block">{validation.errors.permissionid}</div>
        )}
      </Col>
    </Row>
  );
};

export default MenuPermissionForm;
